import { EcoaContent } from "@components/common/EconContent";
import { readableEcoaCrfStatus } from "@lib/functions";
import { sessionSlice } from "@maven-rest/states";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { FxGuard } from "@coralblack/flax";
import { useLangContext } from "@layouts/home/LangContext";
import { StudySpec } from "maven-lib/dist/consts/edc";
import { getCrfLabel } from "maven-lib/dist/consts/edc/lib";
import { EcoaCrf, EcoaCrfStatus } from "maven-lib/dist/dto/entity/ecoaCrf";
import { ecoaCrfApi } from "maven-lib/dist/states/apis/ecoaCrf";
import { CrDialog } from "@maven-surface/components/base/CrDialog";
import { HbList } from "@maven-surface/components/base/HbList";
import { HbListItemProps } from "@maven-surface/components/base/HbListItem";
import { useRoute } from "@maven-surface/hooks/useRoute";
import { CDMS_STUDY_STATUS_CODE } from "@maven-msg/code/cdms/studyStatus";
import { COMMON_LANG } from "@maven-msg/lang/common";
import { MvnMessage } from "@maven-msg/lib/Message";

interface CrfsProps {
  studySpec: StudySpec;
}

type CompletePopupCrf = { crfs: EcoaCrf[]; title: string; text: string };

export function Crfs({ studySpec }: CrfsProps) {
  const { pushPath } = useRoute();
  const dispatch = useDispatch();
  const params = useParams<{ folderId: string; visitId: string; visitSeq: string }>();
  const { folderId, visitId, visitSeq } = params;

  const { fontSize } = useLangContext();

  const ecoaNotice = studySpec.config?.ecoa?.notice;
  const [isShowPopup, setIsShowPopup] = useState(
    !!studySpec.config?.ecoa?.completePopup?.find(({ target }) =>
      target.find((v) => v.folderId === params.folderId && v.visitId === params.visitId && String(v.visitSeq) === params.visitSeq)
    )
  );

  const getCrfElements = (crfs: EcoaCrf[]): Array<HbListItemProps & { key: string | number }> =>
    crfs
      .sort((a, b) => {
        return a.crfPageSeq - b.crfPageSeq;
      })
      .map((crf, crfIdx) => ({
        key: crfIdx,
        elements: [
          { type: "TEXT", text: getCrfLabel(crf.label) },
          {
            type: "CHIP",
            text: !crf.status || crf.status === EcoaCrfStatus.NotAvailable ? null : readableEcoaCrfStatus(crf.status),
            color:
              crf.status === EcoaCrfStatus.Done
                ? "BLACK"
                : crf.status === EcoaCrfStatus.Ongoing
                ? "WARNING"
                : crf.status === EcoaCrfStatus.NotDone
                ? "PRIMARY"
                : "FADE-OUT",
          },
        ],
        accessory: "DISCLOSURE",
        onClick:
          !crf.status || crf.status === EcoaCrfStatus.NotAvailable
            ? false
            : () => {
                const { folderId, visitId, visitSeq, crfPageId, crfPageSeq, status } = crf;

                const isSkipNoticePage = (() => {
                  if (!!ecoaNotice && !Array.isArray(ecoaNotice) && "skipPages" in ecoaNotice) {
                    // skipPages에 정의된 페이지는 안내사항 페이지 skip
                    return ecoaNotice.skipPages.some((page) => {
                      const { folderId: fId, visitId: vId, visitSeq: vSeq, crfPageId: cpId, crfPageSeq: cpSeq } = page;

                      return (
                        ((fId && typeof fId === "object" && fId.reserved === "ANY") || fId === folderId) &&
                        ((vId && typeof vId === "object" && vId.reserved === "ANY") || vId === visitId) &&
                        ((vSeq && typeof vSeq === "object" && vSeq.reserved === "ANY") || vSeq === visitSeq) &&
                        ((cpId && typeof cpId === "object" && cpId.reserved === "ANY") || cpId === crfPageId) &&
                        ((cpSeq && typeof cpSeq === "object" && cpSeq.reserved === "ANY") || cpSeq === crfPageSeq || !cpSeq)
                      );
                    });
                  } else if (!!ecoaNotice && !!Array.isArray(ecoaNotice)) {
                    // target에 정의되어 있지 않은 페이지는 안내사항 페이지 skip
                    return !ecoaNotice.some((notice) => {
                      if ("target" in notice) {
                        const { folderId: fId, visitId: vId, visitSeq: vSeq, crfPageId: cpId, crfPageSeq: cpSeq } = notice.target;

                        return (
                          ((fId && typeof fId === "object" && fId.reserved === "ANY") || fId === folderId) &&
                          ((vId && typeof vId === "object" && vId.reserved === "ANY") || vId === visitId) &&
                          ((vSeq && typeof vSeq === "object" && vSeq.reserved === "ANY") || vSeq === visitSeq) &&
                          ((cpId && typeof cpId === "object" && cpId.reserved === "ANY") || cpId === crfPageId) &&
                          ((cpSeq && typeof cpSeq === "object" && cpSeq.reserved === "ANY") || cpSeq === crfPageSeq || !cpSeq)
                        );
                      }

                      return true;
                    });
                  } else {
                    return false;
                  }
                })();

                isSkipNoticePage
                  ? pushPath(`/pat/visits/${folderId}/${visitId}/${visitSeq}/${crfPageId}/${crfPageSeq}/1`, {
                      from: "crfs",
                    })
                  : pushPath(
                      `/pat/visits/${folderId}/${visitId}/${visitSeq}/${crfPageId}/${crfPageSeq}${
                        status === EcoaCrfStatus.NotDone ? "" : "/1"
                      }`,
                      {
                        from: "crfs",
                      }
                    );
              },
      }));

  return (
    <EcoaContent fontSize={fontSize}>
      <FxGuard
        api={{
          ...ecoaCrfApi.getEcoaCrfs,
          query: { folder_id: folderId, visit_id: visitId, visit_seq: visitSeq, is_all_pages: true },
        }}
        render={(resp) => {
          if (!resp?.crfs.length) return <></>;

          const completedPopup =
            isShowPopup &&
            resp.crfs.reduce<CompletePopupCrf>(
              (arr, crf) => {
                const isTarget = studySpec.config?.ecoa?.completePopup?.find(({ target }) =>
                  target.find(
                    (v) =>
                      v.folderId === crf.folderId &&
                      v.visitId === crf.visitId &&
                      v.visitSeq === crf.visitSeq &&
                      v.crfPageId === crf.crfPageId &&
                      v.crfPageSeq === crf.crfPageSeq
                  )
                );

                if (isTarget) {
                  arr.crfs.push(crf);
                  arr.title = isTarget.title || "";
                  arr.text = isTarget.text;
                }

                return arr;
              },
              { crfs: [], title: "", text: "" }
            );

          return (
            <>
              {completedPopup && completedPopup.crfs.length > 0 && (
                <CrDialog
                  title={completedPopup.title}
                  onConfirm={() => {
                    setIsShowPopup(false);
                  }}
                  visibility={completedPopup.crfs.every((crf) => crf.status === EcoaCrfStatus.Done)}
                  confirm={MvnMessage(COMMON_LANG.CONFIRM_ALTER)}
                >
                  {completedPopup.text}
                </CrDialog>
              )}
              <HbList title={resp.title || ""} items={getCrfElements(resp.crfs)} fontSize={fontSize} />
            </>
          );
        }}
        error={(error) => {
          if (error?.code === CDMS_STUDY_STATUS_CODE.STUDY_CLOSED.code) {
            dispatch(sessionSlice.actions.signOut());

            return error?.message || "An error has occurred.";
          }
        }}
      />
    </EcoaContent>
  );
}
